import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Volume2, Square, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hp-gossip`;

function useReader() {
  const audio = useRef(null);
  const [playing, setPlaying] = useState('');
  const stop = () => { audio.current?.pause(); audio.current = null; setPlaying(''); };
  useEffect(() => stop, []);
  const play = async (id) => {
    if (playing === id) { stop(); return; }
    stop();
    setPlaying(`load:${id}`);
    try {
      const res = await axios.post(`${API}/${id}/read`, {}, { withCredentials: true, responseType: 'blob', timeout: 90000 });
      const a = new Audio(URL.createObjectURL(res.data));
      a.onended = stop;
      audio.current = a;
      setPlaying(id);
      await a.play();
    } catch { toast.error('Farmor tappade rösten en stund 💛'); setPlaying(''); }
  };
  return { playing, play };
}

function ReadBtn({ id, reader }) {
  const st = reader.playing === id ? 'play' : reader.playing === `load:${id}` ? 'load' : '';
  return (
    <button onClick={() => reader.play(id)} className="neo-btn px-3 py-1.5 bg-white text-xs font-bold flex items-center gap-1" data-testid={`hp-gossip-read-${id}`}>
      {st === 'load' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : st === 'play' ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
      {st === 'play' ? 'Stoppa' : 'Läs upp'}
    </button>
  );
}

function PartCard({ p, open, onToggle, reader }) {
  const practice = () => window.dispatchEvent(new CustomEvent('hp-extra-start', { detail: { part_id: p.id } }));
  return (
    <div className="rounded-2xl border-2 border-black bg-white overflow-hidden" data-testid={`hp-gossip-card-${p.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 text-left transition-colors hover:bg-black/[0.03]" aria-expanded={open} data-testid={`hp-gossip-toggle-${p.id}`}>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center text-xl border-2 border-black flex-shrink-0" style={{ background: p.color }}>{p.emoji}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-black opacity-60">{p.name}</span>
          <span className="block font-['Fredoka'] font-bold leading-tight">{p.nick}</span>
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-sm" data-testid={`hp-gossip-body-${p.id}`}>
          <p className="italic">”{p.gossip}”</p>
          <p className="rounded-xl p-2 bg-black/[0.04]"><b>Exempel:</b> {p.example}</p>
          <p className="rounded-xl p-2" style={{ background: `${p.color}66` }}><b>Farmors knep:</b> {p.trick}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <ReadBtn id={p.id} reader={reader} />
            <button onClick={practice} className="neo-btn px-3 py-1.5 bg-[#FFC8DD] text-xs font-bold" data-testid={`hp-gossip-practice-${p.id}`}>💪 Öva på {p.name}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HpGossip() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState('');
  const reader = useReader();
  useEffect(() => { axios.get(API, { withCredentials: true }).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const groups = [...new Set(d.parts.map((p) => p.group))];
  return (
    <section className="neo-card p-4 bg-[#FFF8EE] space-y-3" data-testid="hp-gossip">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h4 className="font-['Fredoka'] font-bold text-lg">☕ Farmor Elsa skvallrar om provet</h4>
          <p className="text-sm opacity-80 max-w-xl">{d.intro}</p>
        </div>
        <ReadBtn id="intro" reader={reader} />
      </div>
      {groups.map((g) => (
        <div key={g} className="space-y-2">
          <p className="text-[11px] font-black uppercase tracking-widest opacity-60">{g}</p>
          <div className="grid sm:grid-cols-2 gap-2 items-start">
            {d.parts.filter((p) => p.group === g).map((p) => (
              <PartCard key={p.id} p={p} open={open === p.id} onToggle={() => setOpen(open === p.id ? '' : p.id)} reader={reader} />
            ))}
          </div>
        </div>
      ))}
      <p className="text-sm font-bold" data-testid="hp-gossip-outro">💛 {d.outro}</p>
    </section>
  );
}
